import {SharedGraph} from '../../navigation/SharedGraph';
import {readSharedReport, useSharedReport} from '../../navigation/useSharedReport';
// src/shared/ui/chart/PieChartWithToggles.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pie, Doughnut } from "react-chartjs-2";
import { useCompactChart } from './useCompactChart';
import { useReportPreference } from './useReportPreference';
import { getInitialVisibleKeys, withAlpha } from "./charts/chartUtils";
import {
  getChartColor,
  CHART_COLOR_MAP,
  CHART_FALLBACK_COLORS,
} from "../../constants/chartColors";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

/** Preferred ordering used when auto-grouping legend entries. */
const GROUP_ORDER = ["positions", "salaries", "services", "materials", "other"];

/** Chart type dropdown options. */
const CHART_TYPE_OPTIONS = [
  { value: "pie", label: "Pie" },
  { value: "doughnut", label: "Doughnut" },
];

/** Pretty titles for known group keys. Falls back to Title Case. */
const GROUP_TITLES = {
  positions: "Positions",
  salaries: "Salaries",
  services: "Purchased Services",
  materials: "Supplies / Materials",
  other: "Other",
};

/**
 * Convert a group key into a reasonably human-friendly Title Case label.
 * - Normalizes underscores/dashes into spaces
 * - Collapses repeated whitespace
 * - Capitalizes each word
 *
 * Note: This is intentionally simple (not locale-aware) and preserves original
 * casing beyond the first letter for each word.
 *
 * @param {string} [key=""] - Group key like "purchased_services" or "materials".
 * @returns {string} Title-cased group label (fallback: "Other").
 */
function titleCaseGroup(key = "") {
  const s = String(key || "").trim();
  if (!s) return "Other";
  return s
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

/**
 * Format a numeric value as US currency with no explicit fractional control.
 * Non-finite values return an em dash.
 *
 * @param {number|string|null|undefined} n
 * @returns {string}
 */
function fmtCurrency(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `$${num.toLocaleString("en-US")}`;
}

/**
 * Format a numeric value as a locale-aware count string.
 * Non-finite values return an em dash.
 *
 * @param {number|string|null|undefined} n
 * @returns {string}
 */
function fmtCount(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-US");
}

/**
 * Format a ratio (0..1) as a percentage string.
 * Non-finite values return an em dash.
 *
 * @param {number|string|null|undefined} ratio
 * @param {number} [digits=1] - Decimal precision.
 * @returns {string}
 */
function fmtPercent(ratio, digits = 1) {
  const x = Number(ratio);
  if (!Number.isFinite(x)) return "—";
  return `${(x * 100).toFixed(digits)}%`;
}

/**
 * Coerce an arbitrary value into a finite number or `null`.
 * This is used throughout to normalize incoming chart data.
 *
 * @param {any} x
 * @returns {number|null}
 */
function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function areStringArraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Select a deterministic color for a given slice.
 *
 * Priority:
 * 1) slice.color (explicit override)
 * 2) stable mapping derived from slice.key (preferred) or slice.label
 *
 * @param {PieSlice} slice
 * @param {string} [fallback=""] - Fallback seed used if slice lacks key/label.
 * @returns {string} CSS color string (hex, rgb, etc.).
 */
function stableColorForSlice(slice, fallback = "") {
  // Explicit per-slice override still wins
  if (slice?.color) return slice.color;

  // Prefer exact IDs (like "0200") if present; otherwise fall back to label.
  const keyBasis = slice?.key || slice?.label || fallback;

  return getChartColor(keyBasis);
}

/**
 * Build a deterministic key->color map ensuring maximal distinctness:
 * - Reserves explicit colors (slice.color) and CHART_COLOR_MAP[key] entries
 * - Assigns remaining keys sequentially from CHART_FALLBACK_COLORS excluding reserved
 *
 * Determinism:
 * - Assignment order follows the incoming `items` order. In this component, that
 *   will be `effectiveItems`, which is already sorted by your `sliceOrder` logic
 *   (and potentially aggregated).
 *
 * @param {PieSlice[]|null|undefined} items
 * @returns {Map<string, string>} Map of slice.key => color.
 */
function buildDistinctColorMap(items) {
  const safe = Array.isArray(items) ? items : [];

  // Reserve explicit colors (slice.color or CHART_COLOR_MAP[slice.key])
  const reserved = new Set();
  const result = new Map();

  safe.forEach((s) => {
    if (!s) return;

    const key = String(s.key || "").trim();
    if (!key) return;

    if (s.color) {
      result.set(key, s.color);
      reserved.add(s.color);
      return;
    }

    if (CHART_COLOR_MAP[key]) {
      result.set(key, CHART_COLOR_MAP[key]);
      reserved.add(CHART_COLOR_MAP[key]);
    }
  });

  // Fallback palette excluding reserved colors
  const palette = (CHART_FALLBACK_COLORS || []).filter((c) => !reserved.has(c));
  if (palette.length === 0) return result;

  // Deterministic assignment order:
  // Use the current effectiveItems order (already sorted by your sliceOrder logic).
  // If you want it stable even when sliceOrder changes, see note below.
  let cursor = 0;

  safe.forEach((s) => {
    const key = String(s?.key || "").trim();
    if (!key) return;
    if (result.has(key)) return; // already assigned explicit color

    result.set(key, palette[cursor % palette.length]);
    cursor += 1;
  });

  return result;
}

/**
 * @typedef {Object} PieSlice
 * @property {string} key               - Unique identifier used for toggles and mapping.
 * @property {string} label             - Human-friendly slice label.
 * @property {number} value             - Numeric slice value.
 * @property {"currency"|"count"|string} [valueType] - Formatting behavior (tooltips/legend).
 * @property {string} [group]           - Category group used for auto-grouping toggles.
 * @property {string} [color]           - Optional fixed slice color (overrides stable mapping).
 * @property {boolean} [defaultVisible] - Optional hint for default check state.
 */

/**
 * Explicit toggle grouping (group checkbox controls multiple slice keys).
 * Used when `groups` prop is provided (manual groups mode).
 *
 * @typedef {Object} SliceToggleGroup
 * @property {string} key - Unique group toggle identifier (for React rendering).
 * @property {string} label - Text shown next to the checkbox.
 * @property {string} [groupKey="other"] - Used to cluster groups under headings.
 * @property {string[]} seriesKeys - Slice keys controlled by this checkbox.
 * @property {boolean} [defaultVisible] - Used by getInitialVisibleKeys() when groups are present.
 */

/**
 * Denominator basis for tooltip percentage calculations.
 * - "all": percent of the sum across all items
 * - "visible": percent of sum across currently visible items
 *
 * @typedef {"all"|"visible"} PercentOfMode
 */

/**
 * Slice ordering mode prior to rendering.
 * - "valueDesc": sort by descending value
 * - "labelAsc": sort by ascending label
 * - "input": preserve input order (no additional sorting)
 *
 * @typedef {"valueDesc"|"labelAsc"|"input"} SliceOrder
 */

/**
 * Legend table placement options (only relevant when showLegendTable is true).
 *
 * @typedef {"right"|"bottom"} LegendTablePlacement
 */

/**
 * Pie/Doughnut chart with checkbox toggles, designed to mirror the behavior
 * and UI conventions of LineChartWithToggles.
 *
 * Typical use: "Share of total" for a single fiscal year (program slices).
 *
 * @component
 * @param {Object} props
 * @param {string} [props.title] - Optional title displayed above the chart.
 * @param {PieSlice[]} [props.items] - Slices to render.
 * @param {string[]} [props.defaultVisibleKeys] - Optional override list of visible slice keys.
 * @param {SliceToggleGroup[]} [props.groups]
 *   Optional explicit groups; when present, enables group-level toggles instead of auto grouping by slice.group.
 *
 * @param {boolean} [props.showAllWhenNoneSelected=true]
 *   If user selects nothing, fall back to showing ALL slices.
 *
 * @param {PercentOfMode} [props.percentOf="all"]
 *   Tooltip percent basis ("all" or "visible").
 *
 * @param {boolean} [props.showLegend=false]
 *   Whether to show Chart.js legend (checkboxes already act as legend).
 *
 * @param {SliceOrder} [props.sliceOrder="valueDesc"]
 *   Sort slices before rendering.
 *
 * @param {boolean} [props.aggregateOthers=false]
 *   If enabled and there are many slices, the remainder is rolled into an "Other" slice.
 * @param {number} [props.maxSlices=12] - Maximum number of slices to show when aggregating.
 * @param {string} [props.otherLabel="Other"] - Label for aggregated remainder slice.
 * @param {string} [props.otherKey="__OTHER__"] - Key for aggregated remainder slice.
 *
 * @param {boolean} [props.showSeriesToggles=true] - Whether to render checkbox UI.
 * @param {boolean} [props.showChartTypeSelector=true] - Whether to show Pie/Doughnut selector.
 * @param {boolean} [props.showTotalValuesSelector=false]
 *   Whether to show a selector that limits the total displayed slices. The final slice
 *   is "Other" when more categories exist than the selected total.
 * @param {number} [props.defaultTotalValues=10]
 *   Initial slice total when the optional total-values selector is enabled.
 *
 * @param {boolean} [props.showLegendTable=false] - Whether to show a legend TABLE next to/below the chart.
 * @param {LegendTablePlacement} [props.legendTablePlacement="right"] - Placement for legend table.
 * @param {string} [props.legendTableMaxHeight="360px"] - Max height for bottom placement scroll container.
 *
 * @param {boolean} [props.includeZeroValues=false]
 *   Include zero values in the LEGEND table. Chart slices always exclude zeros.
 *
 * @param {boolean} [props.enableHoverPop=true] - Enable hover pop-out effect.
 * @param {number} [props.hoverPopOffset=12] - Pop-out offset in pixels.
 *
 * @returns {JSX.Element}
 */
export function PieChartWithToggles(props) {
  return <SharedGraph title={props.title} shareId={props.shareId}>{id => <SharedPieChart key={id} {...props} shareId={id}/>}</SharedGraph>;
}
function SharedPieChart({
  shareId,
  title,
  items,
  defaultVisibleKeys,
  groups,
  showOutsideLabels = false,
  titleBar = false,

  /** If user selects nothing, fall back to showing ALL slices (default: true). */
  showAllWhenNoneSelected = true,

  /** Tooltip percent basis: "all" (default) or "visible". */
  percentOf = "all",

  /** Whether to show Chart.js legend (checkboxes already act as legend). */
  showLegend = false,

  /** Sort slices before rendering: "valueDesc" | "labelAsc" | "input" */
  sliceOrder = "valueDesc",

  /**
   * Optional aggregation to reduce visual noise.
   * If enabled and there are many slices, the remainder is rolled into "Other".
   */
  aggregateOthers = false,
  maxSlices = 12,
  otherLabel = "Other",
  otherKey = "__OTHER__",

  /** Hide the slice checkbox UI entirely (default: true). */
  showSeriesToggles = true,

  /** Hide the Pie/Doughnut selector (default: true). */
  showChartTypeSelector = true,

  /** Optionally let the user choose the total number of displayed slices. */
  showTotalValuesSelector = false,
  defaultTotalValues = 10,

  /** Initial chart type when the selector is hidden or on first render. */
  initialChartType = "pie",

  /** Show a legend TABLE next to the chart (default: false). */
  showLegendTable = false,

  /** Where to place the legend table: "right" | "bottom" */
  legendTablePlacement = "right",

  /** Legend table max height (scroll) — used for bottom placement; side placement fills chart height */
  legendTableMaxHeight = "360px",

  /**
   * Include zero values in the LEGEND table.
   * Chart slices always exclude zeros to avoid clutter / zero-width slices.
   */
  includeZeroValues = false,

  /** Hover pop-out effect */
  enableHoverPop = true,
  hoverPopOffset = 12,
}) {
  const [shared] = useState(() => readSharedReport(shareId));
  const compact = useCompactChart();
  const outsideLabels = showOutsideLabels && !compact;
  /**
   * “Groups mode” is enabled when an explicit groups prop is provided.
   * In this mode, toggles are rendered by group and control multiple slice keys at once.
   *
   * @type {boolean}
   */
  const usingGroups = Array.isArray(groups) && groups.length > 0;

  /**
   * Defensive: always treat items as an array for downstream logic.
   *
   * @type {PieSlice[]}
   */
  const safeItems = Array.isArray(items) ? items : [];

  /**
   * Reference to the underlying Chart.js instance (via react-chartjs-2).
   * Currently not used for imperative calls but kept available for future enhancements.
   *
   * @type {React.MutableRefObject<any>}
   */
  const chartRef = useRef(null);

  /** Selected chart type (pie/doughnut). */
  const [chartType, setChartType] = useReportPreference(`${title || 'pie'}:pie-type`,
    initialChartType === "doughnut" ? "doughnut" : "pie",
    ["pie","doughnut"].includes(shared.type) ? shared.type : undefined
  );

  /**
   * Track whether the user has interacted with toggles.
   * If false, we can safely re-initialize defaults when async data arrives.
   */
  const [hasUserToggled, setHasUserToggled] = useReportPreference(`${title || "pie"}:pie-toggled`, false, Array.isArray(shared.series) ? true : undefined);

  /**
   * Hovered slice index (Chart.js dataset index), used to apply the pop-out offset.
   * null means no slice is active.
   *
   * @type {[number|null, React.Dispatch<React.SetStateAction<number|null>>]}
   */
  const [hoverIndex, setHoverIndex] = useState(null);

  /**
   * Which slice keys are currently visible.
   *
   * Initialization rules:
   * - If toggles are hidden: show everything (don’t surprise the user).
   * - Else: compute from getInitialVisibleKeys (if available), falling back to all keys.
   *
   * @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]}
   */
  const [visibleKeys, setVisibleKeys] = useReportPreference(`${title || "pie"}:pie-visible`, () => {
    // If toggles are hidden, we always show everything.
    if (!showSeriesToggles) {
      return safeItems.map((s) => s.key).filter(Boolean);
    }

    const init =
      typeof getInitialVisibleKeys === "function"
        ? getInitialVisibleKeys(safeItems, defaultVisibleKeys, groups)
        : [];

    if (!Array.isArray(init) || init.length === 0) {
      return safeItems.map((s) => s.key).filter(Boolean);
    }
    return init;
  }, Array.isArray(shared.series) ? shared.series.filter(key => safeItems.some(item => item.key === key)) : undefined);

  /**
   * When the backing items change (e.g., async Firestore load), re-initialize visibility
   * only if the user has not already interacted with the toggles.
   *
   * Special-case: when toggles are hidden, always keep everything visible.
   */
  useEffect(() => {
    const allKeys = safeItems.map((s) => s.key).filter(Boolean);

    // If toggles are hidden, keep everything visible.
    if (!showSeriesToggles) {
      setVisibleKeys((prev) =>
        areStringArraysEqual(prev, allKeys) ? prev : allKeys
      );
      return;
    }

    if (hasUserToggled) return;

    const init =
      typeof getInitialVisibleKeys === "function"
        ? getInitialVisibleKeys(safeItems, defaultVisibleKeys, groups)
        : [];

    if (!Array.isArray(init) || init.length === 0) {
      setVisibleKeys((prev) =>
        areStringArraysEqual(prev, allKeys) ? prev : allKeys
      );
      return;
    }
    setVisibleKeys((prev) =>
      areStringArraysEqual(prev, init) ? prev : init
    );
  }, [
    safeItems,
    defaultVisibleKeys,
    groups,
    hasUserToggled,
    showSeriesToggles,
  ]);

  /**
   * Toggle a single slice key on/off.
   *
   * @param {string} key
   * @returns {void}
   */
  const toggleKey = (key) => {
    if (!key) return;
    setHasUserToggled(true);
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  /**
   * Toggle an entire group of keys on/off.
   * If all seriesKeys are currently visible => turn all off; else turn all on.
   *
   * @param {SliceToggleGroup} group
   * @returns {void}
   */
  const toggleGroup = (group) => {
    if (!group || !Array.isArray(group.seriesKeys)) return;
    setHasUserToggled(true);
    setVisibleKeys((prev) => {
      const set = new Set(prev);
      const allVisible =
        group.seriesKeys.length > 0 &&
        group.seriesKeys.every((k) => set.has(k));

      if (allVisible) group.seriesKeys.forEach((k) => set.delete(k));
      else group.seriesKeys.forEach((k) => set.add(k));

      return Array.from(set);
    });
  };

  /**
   * Auto-group items by slice.group when explicit `groups` are not provided.
   * Output format: Array<[groupKey, PieSlice[]]>
   *
   * In manual groups mode, this returns [] and the UI uses manualGroupedToggles instead.
   */
  const groupedItems = useMemo(() => {
    if (usingGroups) return [];

    const map = new Map();
    safeItems.forEach((s) => {
      const g = s.group || "other";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(s);
    });

    for (const list of map.values()) {
      list.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }

    const entries = Array.from(map.entries());
    entries.sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a[0]);
      const bi = GROUP_ORDER.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return entries;
  }, [safeItems, usingGroups]);

  /**
   * Manual grouped toggles derived from explicit `groups` prop.
   * Output format: Array<[groupKey, SliceToggleGroup[]]>
   */
  const manualGroupedToggles = useMemo(() => {
    if (!usingGroups) return [];

    const map = new Map();
    (groups || []).forEach((g) => {
      const key = g.groupKey || "other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(g);
    });

    for (const list of map.values()) {
      list.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }

    const entries = Array.from(map.entries());
    entries.sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a[0]);
      const bi = GROUP_ORDER.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return entries;
  }, [groups, usingGroups]);

  /**
   * Compute total sums used for percent denominators.
   * - all: sum of all safeItems
   * - visible: sum of items whose key is currently visible
   */
  const totals = useMemo(() => {
    const all = safeItems.reduce(
      (acc, s) => acc + (safeNumber(s.value) ?? 0),
      0
    );
    const visible = safeItems
      .filter((s) => visibleKeys.includes(s.key))
      .reduce((acc, s) => acc + (safeNumber(s.value) ?? 0), 0);
    return { all, visible };
  }, [safeItems, visibleKeys]);

  /**
   * Base list after visibility rules, BEFORE zero filtering.
   * Used for legend table (when includeZeroValues = true).
   */
  const selectedItems = useMemo(() => {
    let list = safeItems.filter((s) => visibleKeys.includes(s.key));
    if (list.length === 0 && showAllWhenNoneSelected) list = safeItems;

    // normalize values
    list = list
      .map((s) => ({ ...s, value: safeNumber(s.value) ?? 0 }))
      .filter((s) => Number.isFinite(s.value));

    if (sliceOrder === "labelAsc") {
      list = [...list].sort((a, b) =>
        String(a.label).localeCompare(String(b.label))
      );
    } else if (sliceOrder === "valueDesc") {
      list = [...list].sort((a, b) => (b.value || 0) - (a.value || 0));
    }

    return list;
  }, [safeItems, visibleKeys, showAllWhenNoneSelected, sliceOrder]);

  const availableSliceCount = useMemo(
    () => selectedItems.filter((s) => (safeNumber(s.value) ?? 0) !== 0).length,
    [selectedItems]
  );
  const [requestedTotalValues, setRequestedTotalValues] = useReportPreference(`${title || "pie"}:pie-count`, () => {
    const parsed = Number(defaultTotalValues);
    return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 10;
  }, Number.isInteger(shared.count) && shared.count > 0 ? shared.count : undefined);
  const activateShare = useSharedReport(shareId, undefined, {type:chartType, series:visibleKeys, count:requestedTotalValues});
  const minimumTotalValues = availableSliceCount > 1 ? 2 : availableSliceCount;
  const totalValues = availableSliceCount
    ? Math.min(Math.max(minimumTotalValues, requestedTotalValues), availableSliceCount)
    : 0;
  const totalValueOptions = useMemo(
    () => Array.from(
      { length: Math.max(0, availableSliceCount - minimumTotalValues + 1) },
      (_, index) => index + minimumTotalValues
    ),
    [availableSliceCount, minimumTotalValues]
  );

  /**
   * Items actually used by the CHART.
   * Always excludes zero values to avoid clutter/zero-width slices.
   */
  const effectiveItems = useMemo(() => {
    let list = selectedItems.filter((s) => (safeNumber(s.value) ?? 0) !== 0);

    if (
      (showTotalValuesSelector || aggregateOthers) &&
      Number.isFinite(showTotalValuesSelector ? totalValues : maxSlices) &&
      (showTotalValuesSelector ? totalValues : maxSlices) >= 2 &&
      list.length > (showTotalValuesSelector ? totalValues : maxSlices)
    ) {
      const sliceLimit = showTotalValuesSelector ? totalValues : maxSlices;
      const head = list.slice(0, sliceLimit - 1);
      const tail = list.slice(sliceLimit - 1);
      const otherSum = tail.reduce(
        (acc, s) => acc + (safeNumber(s.value) ?? 0),
        0
      );
      if (otherSum > 0) {
        head.push({
          key: otherKey,
          label: otherLabel,
          value: otherSum,
          valueType: head[0]?.valueType || "currency",
          group: "other",
        });
      }
      return head;
    }

    return list;
  }, [selectedItems, aggregateOthers, maxSlices, otherKey, otherLabel, showTotalValuesSelector, totalValues]);

  /**
   * Color assignment for slices.
   * This creates a stable, deterministic palette assignment for slices without explicit colors.
   */
  const colorByKey = useMemo(
    () => buildDistinctColorMap(effectiveItems),
    [effectiveItems]
  );

  /**
   * Denominator used for tooltip percent calculation based on percentOf mode.
   */
  const denominator = useMemo(() => {
    if (percentOf === "visible") return totals.visible;
    return totals.all;
  }, [percentOf, totals]);

  /**
   * Map of slice.key -> chart index position within effectiveItems.
   * Used to sync hover highlighting between chart and legend table.
   */
  const chartIndexByKey = useMemo(() => {
    const map = new Map();
    effectiveItems.forEach((s, idx) => {
      if (s?.key) map.set(s.key, idx);
    });
    return map;
  }, [effectiveItems]);

  /**
   * Chart.js data model (labels + datasets).
   * Applies:
   * - per-slice colors (withAlpha for fill/border)
   * - hover pop offset (Chart.js ArcElement offset)
   */
  const chartData = useMemo(() => {
    const labels = effectiveItems.map((s) => s.label);
    const data = effectiveItems.map((s) => s.value);

    const bg = effectiveItems.map((s) => {
      const base = colorByKey.get(s.key) || getChartColor(s.key || s.label);
      return withAlpha(base, 0.9);
    });

    const border = effectiveItems.map((s) => {
      const base = colorByKey.get(s.key) || getChartColor(s.key || s.label);
      return withAlpha(base, 1.0);
    });

    const offsets =
      enableHoverPop && Number.isFinite(hoverPopOffset)
        ? effectiveItems.map((_, i) => (i === hoverIndex ? hoverPopOffset : 0))
        : effectiveItems.map(() => 0);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: bg,
          borderColor: border,
          borderWidth: 1,
          hoverBorderWidth: 2,
          offset: offsets,
        },
      ],
    };
  }, [effectiveItems, colorByKey, enableHoverPop, hoverPopOffset, hoverIndex]);
  const chartRenderKey = useMemo(
    () => effectiveItems.map((item) => `${item.key}:${item.value}`).join("|"),
    [effectiveItems]
  );

  /**
   * Chart.js options model.
   * - Tooltip label includes:
   *   label, formatted value, and percent-of (based on denominator)
   * - onHover updates hoverIndex used by hover pop effect
   */
  const options = useMemo(
    () => ({
      responsive: true,
      layout: outsideLabels
        ? { padding: { top: 34, right: 92, bottom: 34, left: 92 } }
        : undefined,
      plugins: {
        legend: { display: !!showLegend && !compact, position: "right" },
        tooltip: {
          callbacks: {
            /**
             * Tooltip label callback.
             *
             * @param {any} ctx - Chart.js tooltip context
             * @returns {string}
             */
            label: (ctx) => {
              const idx = ctx.dataIndex;
              const slice = effectiveItems[idx];
              const raw = safeNumber(ctx.raw) ?? 0;
              const valueType = slice?.valueType || "currency";
              const denom = Number(denominator) || 0;
              const pct = denom > 0 ? raw / denom : 0;

              const valText =
                valueType === "count" ? fmtCount(raw) : fmtCurrency(raw);

              return `${ctx.label}: ${valText} (${fmtPercent(pct)})`;
            },
          },
        },
      },

      /**
       * Chart hover handler used to drive hover pop offsets.
       *
       * @param {any} _evt
       * @param {Array<{index?: number}>} activeEls
       */
      onHover: (_evt, activeEls) => {
        if (!enableHoverPop) return;
        const idx =
          Array.isArray(activeEls) && activeEls.length
            ? activeEls[0]?.index
            : null;
        setHoverIndex(Number.isFinite(idx) ? idx : null);
      },

      animation: {
        duration: 180,
      },
    }),
    [showLegend, effectiveItems, denominator, enableHoverPop, outsideLabels, compact]
  );

  const outsideLabelsPlugin = useMemo(() => ({
    id: "outside-pie-labels",
    afterDatasetsDraw(chart) {
      if (!showOutsideLabels) return;
      const arcs = chart.getDatasetMeta(0)?.data || [];
      const labels = arcs.map((arc, index) => {
        const angle = (arc.startAngle + arc.endAngle) / 2;
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        return {
          arc,
          label: String(effectiveItems[index]?.label || ""),
          right: cosine >= 0,
          cosine,
          sine,
          desiredY: arc.y + sine * (arc.outerRadius + 20),
        };
      }).filter((item) => item.label);

      for (const right of [false, true]) {
        const side = labels.filter((item) => item.right === right).sort((a, b) => a.desiredY - b.desiredY);
        const minimumGap = 22;
        const minimumY = 16;
        const maximumY = chart.height - 16;
        side.forEach((item, index) => {
          item.labelY = Math.max(item.desiredY, index ? side[index - 1].labelY + minimumGap : minimumY);
        });
        for (let index = side.length - 1; index >= 0; index--) {
          const ceiling = index === side.length - 1 ? maximumY : side[index + 1].labelY - minimumGap;
          side[index].labelY = Math.min(side[index].labelY, ceiling);
        }
      }

      const { ctx } = chart;
      ctx.save();
      ctx.font = "500 13px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
      ctx.fillStyle = "#4b5563";
      ctx.strokeStyle = "#6b7280";
      ctx.lineWidth = 1;
      ctx.textBaseline = "middle";
      for (const item of labels) {
        const startX = item.arc.x + item.cosine * item.arc.outerRadius;
        const startY = item.arc.y + item.sine * item.arc.outerRadius;
        const elbowX = item.arc.x + item.cosine * (item.arc.outerRadius + 12);
        const textWidth = ctx.measureText(item.label).width;
        const textX = item.right ? chart.width - 10 : 10;
        const lineEndX = item.right ? textX - textWidth - 5 : textX + textWidth + 5;
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(elbowX, item.labelY);
        ctx.lineTo(lineEndX, item.labelY);
        ctx.stroke();
        ctx.textAlign = item.right ? "right" : "left";
        ctx.fillText(item.label, textX, item.labelY);
      }
      ctx.restore();
    },
  }), [effectiveItems, showOutsideLabels]);

  /**
   * Legend table rows derived from selectedItems (optionally including zeros).
   * Each row includes:
   * - percent (of denominator)
   * - stable color (aligned with chart if possible)
   * - chartIdx mapping for hover sync
   */
  const legendRows = useMemo(() => {
    const denom = Number(denominator) || 0;

    const base = includeZeroValues
      ? selectedItems
      : selectedItems.filter((s) => (safeNumber(s.value) ?? 0) !== 0);

    return base.map((s) => {
      const val = safeNumber(s.value) ?? 0;
      const pct = denom > 0 ? val / denom : 0;

      return {
        key: s.key || s.label,
        label: s.label,
        pct,
        color: colorByKey.get(s.key) || stableColorForSlice(s),
        chartIdx: s.key ? chartIndexByKey.get(s.key) ?? null : null,
      };
    });
  }, [
    selectedItems,
    includeZeroValues,
    denominator,
    chartIndexByKey,
    colorByKey,
  ]);

  const showSideLegend = showLegendTable && legendTablePlacement === "right";
  const showBottomLegend = showLegendTable && legendTablePlacement === "bottom";

  return (
    <div onChange={activateShare} onClick={activateShare} className="mt-3 border border-gray-300 rounded bg-white">
      <div className={`${titleBar ? "bg-blue-900 px-4 py-2 text-white" : "px-4 pt-2 pb-1"} flex flex-wrap gap-3 items-center justify-between`}>
        {title ? <h3 className={`${titleBar ? "text-xl font-medium" : "text-lg font-semibold"}`}>{title}</h3> : <div />}

        {(showTotalValuesSelector || showChartTypeSelector) ? (
          <div className="flex flex-wrap items-center justify-end gap-3 text-xs">
            {showTotalValuesSelector && availableSliceCount > 0 && <label className="flex items-center gap-2">
              <span className={titleBar ? "text-white" : "text-gray-600"}>Total values:</span>
              <select
                aria-label="Total values"
                value={totalValues}
                onChange={(event) => setRequestedTotalValues(Number(event.target.value))}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900"
              >
                {totalValueOptions.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>}
            {showChartTypeSelector && <label className="flex items-center gap-2">
            <span className={titleBar ? "text-white" : "text-gray-600"}>Chart type:</span>
            <select
              aria-label="Chart type"
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 bg-white text-xs text-gray-900"
            >
              {CHART_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            </label>}
          </div>
        ) : (
          <div />
        )}
      </div>

      <div className="px-4 pt-1">
        {showSeriesToggles && (
          <div className="flex flex-col gap-1 text-xs mb-2">
            {usingGroups
              ? manualGroupedToggles.map(([groupKey, list]) => (
                  <div
                    key={groupKey}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1"
                  >
                    {list.map((g) => {
                      const allVisible =
                        Array.isArray(g.seriesKeys) &&
                        g.seriesKeys.length > 0 &&
                        g.seriesKeys.every((k) => visibleKeys.includes(k));

                      return (
                        <label
                          key={g.key}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={allVisible}
                            onChange={() => toggleGroup(g)}
                            className="rounded border-gray-400"
                          />
                          <span>{g.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ))
              : groupedItems.map(([groupKey, list]) => {
                  const groupTitle =
                    GROUP_TITLES[groupKey] || titleCaseGroup(groupKey);

                  return (
                    <div key={groupKey} className="mb-3">
                      <div className="text-[11px] font-semibold text-gray-700 mb-1">
                        {groupTitle}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                        {list.map((s) => (
                          <label
                            key={s.key}
                            className="flex items-center gap-2 cursor-pointer"
                            title={s.label}
                          >
                            <input
                              type="checkbox"
                              checked={visibleKeys.includes(s.key)}
                              onChange={() => toggleKey(s.key)}
                              className="rounded border-gray-400"
                            />
                            <span className="leading-4">{s.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
          </div>
        )}
      </div>

      <div
        className={`p-3 w-full ${
          showSideLegend
            ? "grid grid-cols-1 lg:grid-cols-[minmax(320px,1fr)_420px] gap-4 items-stretch"
            : ""
        }`}
        onMouseLeave={() => setHoverIndex(null)}
      >
        {/* Chart */}
        <div className="w-full overflow-x-auto h-full">
          {chartType === "doughnut" ? (
            <Doughnut key={`doughnut-${chartRenderKey}-${compact}`} ref={chartRef} data={chartData} options={options} plugins={outsideLabels ? [outsideLabelsPlugin] : []} />
          ) : (
            <Pie key={`pie-${chartRenderKey}-${compact}`} ref={chartRef} data={chartData} options={options} plugins={outsideLabels ? [outsideLabelsPlugin] : []} />
          )}
        </div>

        {compact && (showLegend || showOutsideLabels) && <ul aria-label="Chart values" className="mt-3 space-y-2 text-sm text-slate-800">
          {chartData.labels.map((label, index) => <li key={`${label}-${index}`} className="flex items-start gap-2">
            <span aria-hidden="true" className="mt-1 h-3 w-3 shrink-0 rounded-sm" style={{backgroundColor: chartData.datasets[0].backgroundColor[index]}} />
            <span className="min-w-0 flex-1 break-words">{label}</span><span className="font-semibold tabular-nums">{Number(chartData.datasets[0].data[index]).toLocaleString()}</span>
          </li>)}
        </ul>}
        {/* Legend table (right) */}
        {showSideLegend && (
          <div className="border border-gray-200 rounded bg-white h-full flex flex-col">
            {/* Fill to match chart height; scroll inside */}
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b border-gray-200">
                  <tr>
                    <th className="text-left font-semibold text-gray-600 px-3 py-2">
                      Program
                    </th>
                    <th className="text-right font-semibold text-gray-600 px-3 py-2">
                      %
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {legendRows.map((r) => {
                    const isHot =
                      Number.isFinite(r.chartIdx) && hoverIndex === r.chartIdx;

                    return (
                      <tr
                        key={r.key}
                        className={`border-b border-gray-100 cursor-pointer ${
                          isHot ? "bg-gray-50" : "hover:bg-gray-50"
                        }`}
                        onMouseEnter={() => {
                          // Only pop a real slice
                          if (Number.isFinite(r.chartIdx))
                            setHoverIndex(r.chartIdx);
                          else setHoverIndex(null);
                        }}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded-sm"
                              style={{ backgroundColor: r.color }}
                            />
                            <span className="leading-4">{r.label}</span>
                          </div>
                        </td>

                        <td className="px-3 py-2 text-right tabular-nums">
                          {fmtPercent(r.pct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legend table (bottom) */}
        {showBottomLegend && (
          <div className="border border-gray-200 rounded bg-white mt-3">
            <div
              className="overflow-auto"
              style={{ maxHeight: legendTableMaxHeight }}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b border-gray-200">
                  <tr>
                    <th className="text-left font-semibold text-gray-600 px-3 py-2">
                      Program
                    </th>
                    <th className="text-right font-semibold text-gray-600 px-3 py-2">
                      %
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {legendRows.map((r) => {
                    const isHot =
                      Number.isFinite(r.chartIdx) && hoverIndex === r.chartIdx;

                    return (
                      <tr
                        key={r.key}
                        className={`border-b border-gray-100 cursor-pointer ${
                          isHot ? "bg-gray-50" : "hover:bg-gray-50"
                        }`}
                        onMouseEnter={() => {
                          if (Number.isFinite(r.chartIdx))
                            setHoverIndex(r.chartIdx);
                          else setHoverIndex(null);
                        }}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded-sm"
                              style={{ backgroundColor: r.color }}
                            />
                            <span className="leading-4">{r.label}</span>
                          </div>
                        </td>

                        <td className="px-3 py-2 text-right tabular-nums">
                          {fmtPercent(r.pct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
